/**
 * RESPONSIBILITY: turn enabled flags into a short string, and back.
 *
 *   "A4b"  →  the flags coded A, 4 and b are ON
 *
 * A SET, not a positional encoding — so order does not matter, `"4A" === "A4"`, and
 * there is no arithmetic, no bit shifting and no BigInt. Adding a flag cannot disturb
 * any existing code.
 *
 * ENABLE-ONLY BY CONSTRUCTION: a code lists what to switch ON. There is no way to
 * express "turn this off", so a shared link can only ever add to what the recipient
 * already has.
 */
import { allFlags, flagByCode, flagByKey } from './registry';
import { FLAG_ALPHABET } from './alphabet';

/** Enabled flags → their codes, in alphabet order. */
export function encodeFlags(values: Record<string, boolean>): string {
  return allFlags()
    .filter((f) => values[f.key])
    .map((f) => f.code)
    .join('');
}

export interface DecodedCode {
  /** Keys of flags to enable. */
  keys: string[];
  /** Characters that match no registered flag — reported, not silently dropped. */
  unknown: string[];
}

export function decodeFlags(code: string): DecodedCode {
  const keys: string[] = [];
  const unknown: string[] = [];

  // Case-sensitive: 'a' and 'A' are different flags.
  for (const ch of code.trim()) {
    const flag = flagByCode(ch);
    if (flag) keys.push(flag.key);
    else unknown.push(ch);
  }

  return { keys: [...new Set(keys)], unknown: [...new Set(unknown)] };
}

export const codeFor = (key: string): string | undefined => flagByKey(key)?.code;

export const describeCode = (code: string): string => {
  const { keys, unknown } = decodeFlags(code);
  const parts = keys.length ? `enables ${keys.join(', ')}` : 'enables nothing';
  return unknown.length ? `${parts} · unknown: ${unknown.join(' ')}` : parts;
};

/** True if every character maps to a registered flag. */
export const isValidCode = (code: string): boolean => {
  const t = code.trim();
  return t.length > 0 && [...t].every((c) => Boolean(flagByCode(c)));
};

export { FLAG_ALPHABET };
